import type {
  ProficiencyFilterLevel,
  ProficiencyLevel,
  Settings,
} from "@/stores/InterfaceSetting";
import { I18n } from "@/util/translations";

const level_map = {
  3: [1, 3, 5],
  5: [1, 2, 3, 4, 5],
} as const;

export class Proficiency {
  private readonly interface_setting: Settings;

  constructor(interface_setting: Settings) {
    this.interface_setting = interface_setting;
  }

  update_labels(labels: Settings["skill_proficiency_labels"]) {
    this.interface_setting.skill_proficiency_labels = labels;
  }

  enabled() {
    return this.interface_setting.enable_skill_proficiency === true;
  }

  default_label_for_level(
    level: ProficiencyFilterLevel,
    key: "name" | "description",
    lang: string | null = null,
  ) {
    const i18n_key = `skill_proficiency.level_${level}.${key}` as const;
    const int_label = I18n.t(i18n_key, { locale: "en" });
    return I18n.t(i18n_key, { locale: lang, defaultValue: int_label });
  }

  label_for(
    level: ProficiencyFilterLevel,
    lang: string,
    key: "name" | "description",
  ) {
    if (!this.interface_setting.skill_proficiency_labels) {
      return "";
    }

    if (level === 0) {
      return "";
    }

    const labels = this.interface_setting.skill_proficiency_labels;
    if (!labels) {
      return "";
    }

    const labels_for_lang = labels[lang];
    if (!labels_for_lang) {
      return "";
    }

    const labels_for_level = labels_for_lang[level];
    if (!labels_for_level) {
      return "";
    }
    return labels_for_level[key] || "";
  }

  label_or_default_for(
    level: ProficiencyFilterLevel,
    lang: string,
    field?: "name" | "description" | null,
  ): string {
    const resolvedField = field ?? "name";
    return (
      this.label_for(level, lang, resolvedField) ||
      this.default_label_for_level(level, resolvedField, lang)
    );
  }

  levels(): readonly ProficiencyLevel[] {
    if (!this.interface_setting.skill_proficiency_levels) {
      return [];
    }
    return level_map[this.interface_setting.skill_proficiency_levels];
  }

  current_level() {
    return this.interface_setting.skill_proficiency_levels ?? 3;
  }
}
